import { useEffect, useState } from "react";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from "./ui/dialog";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "./ui/select";
import { Mail, Building2, Calendar, Save, UserCircle } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { CONTACT_STATUSES, getStatusInfo, formatDateTime } from "../lib/adminConstants";

export default function ContactDetailModal({ contact, open, onOpenChange, onUpdated }) {
    const [status, setStatus] = useState("new");
    const [notes, setNotes] = useState("");
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (contact) {
            setStatus(contact.status || "new");
            setNotes(contact.notes || "");
        }
    }, [contact]);

    if (!contact) return null;
    const statusInfo = getStatusInfo(CONTACT_STATUSES, status);

    const save = async () => {
        setSaving(true);
        try {
            await api.patch(`/admin/contacts/${contact.id}`, { status, notes });
            toast.success("Aktualisiert");
            onUpdated?.({ ...contact, status, notes });
            onOpenChange(false);
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                data-testid="contact-detail-modal"
                className="max-w-2xl max-h-[90vh] overflow-y-auto p-0 gap-0"
            >
                <div className="bg-aimploy-navy text-white p-6 lg:p-8 rounded-t-lg">
                    <DialogHeader>
                        <DialogTitle className="font-display text-2xl lg:text-3xl font-bold text-white">
                            {contact.name}
                        </DialogTitle>
                        <DialogDescription className="text-white/70 mt-1">
                            Anfrage als <strong className="text-white capitalize">{contact.user_type === "company" ? "Unternehmen" : "Kandidat"}</strong>
                        </DialogDescription>
                    </DialogHeader>
                    <div className="inline-flex items-center gap-2 mt-4 px-3 py-1.5 rounded-full text-xs font-semibold bg-white/10 border border-white/20 text-white">
                        <span className="h-2 w-2 rounded-full bg-aimploy-violet" />
                        {statusInfo.label}
                    </div>
                </div>

                <div className="p-6 lg:p-8 space-y-6">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <Field icon={Mail} label="E-Mail" value={<a href={`mailto:${contact.email}`} className="hover:text-aimploy-violet">{contact.email}</a>} />
                        {contact.company && <Field icon={Building2} label="Unternehmen" value={contact.company} />}
                        <Field icon={UserCircle} label="Typ" value={contact.user_type === "company" ? "Unternehmen" : "Kandidat"} />
                        <Field icon={Calendar} label="Eingegangen" value={formatDateTime(contact.created_at)} />
                    </div>

                    <div>
                        <div className="text-xs uppercase tracking-[0.15em] text-aimploy-muted font-semibold mb-2">Nachricht</div>
                        <div className="bg-aimploy-bg rounded-xl p-4 text-sm leading-relaxed whitespace-pre-line">
                            {contact.message}
                        </div>
                    </div>

                    <div>
                        <div className="text-xs uppercase tracking-[0.15em] text-aimploy-muted font-semibold mb-2">Status</div>
                        <Select value={status} onValueChange={setStatus}>
                            <SelectTrigger data-testid="contact-status-select" className="w-full">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {CONTACT_STATUSES.map((s) => (
                                    <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>

                    <div>
                        <div className="text-xs uppercase tracking-[0.15em] text-aimploy-muted font-semibold mb-2">Interne Notizen</div>
                        <textarea
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            rows={4}
                            data-testid="contact-notes"
                            placeholder="Was wurde besprochen? Welche nächsten Schritte?"
                            className="w-full border border-aimploy-navy/15 rounded-xl px-4 py-3 text-sm resize-none"
                        />
                    </div>

                    <div className="flex gap-3 pt-2">
                        <button onClick={() => onOpenChange(false)} className="flex-1 px-5 py-3 rounded-xl border border-aimploy-navy/15 text-sm font-semibold hover:bg-aimploy-bg">
                            Schließen
                        </button>
                        <a
                            href={`mailto:${contact.email}?subject=Re: Ihre Anfrage bei aimploy`}
                            className="flex-1 inline-flex items-center justify-center gap-2 bg-aimploy-bg text-aimploy-navy border border-aimploy-navy/15 px-5 py-3 rounded-xl text-sm font-semibold hover:bg-white"
                        >
                            <Mail className="w-4 h-4" /> Antworten
                        </a>
                        <button
                            onClick={save}
                            disabled={saving}
                            className="flex-1 inline-flex items-center justify-center gap-2 bg-aimploy-navy text-white px-5 py-3 rounded-xl text-sm font-semibold hover:bg-aimploy-navy-hover disabled:opacity-60"
                        >
                            <Save className="w-4 h-4" />
                            {saving ? "Speichern…" : "Speichern"}
                        </button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}

function Field({ icon: Icon, label, value }) {
    return (
        <div className="flex items-start gap-3">
            <div className="h-8 w-8 rounded-lg bg-aimploy-violet/10 text-aimploy-violet flex items-center justify-center flex-shrink-0">
                <Icon className="w-4 h-4" />
            </div>
            <div className="min-w-0">
                <div className="text-xs uppercase tracking-[0.15em] text-aimploy-muted font-semibold mb-0.5">{label}</div>
                <div className="text-sm font-medium truncate">{value}</div>
            </div>
        </div>
    );
}
